import { useEffect, useMemo } from "react";
import { Schedule } from "@mui/icons-material";
import { ToggleButtonGroup, ToggleButton, Tooltip, Box, Alert } from "@mui/material";
import TimePicker from "react-time-picker";

function splitOnce(str: string, delimiter: string): [string, string | undefined] {
  const index = str.indexOf(delimiter);
  if (index === -1) return [str, undefined];
  return [str.slice(0, index), str.slice(index + delimiter.length)];
}

function extractManualTimeXB(v: string) {
  // "xB07:00+10+30+0" -> "07:00"
  return v.substring(2).split("+")[0];
}

export default function SpecialTimePicker({
  value,
  onChange,
  routeInfo,
  imTakt = false,
  defaultTime = "07:00",
}: {
  value: string | null;
  imTakt?: boolean;
  onChange: (value: string | null) => void;
  routeInfo: {
    loadingTime: number | null;
    routeTime: number | null;
    inversed: boolean;
    plantAvailability?: {
      isClosingDay: boolean;
      closingDayComment: string | null;
      earliestLoadingTime: string | null;
    };
  } | null;
  defaultTime?: string;
}) {
  // Build dynamic suffix (no leading '+')
  const addition = useMemo(() => {
    if (!routeInfo) return null;
    const { loadingTime, routeTime, inversed } = routeInfo;
    if (loadingTime === null || routeTime === null) return null;
    return `${loadingTime}+${routeTime}+${inversed ? "1" : "0"}`;
  }, [routeInfo]);

  // Keep xB manual time but refresh suffix when routeInfo changes
  useEffect(() => {
    if (!addition || !value || !value.startsWith("xB")) return;
    const [manual, currentAddition] = splitOnce(value, "+"); // manual = "xB07:00"
    if (currentAddition === addition) return;
    onChange(`${manual}+${addition}`);
  }, [addition, value, onChange]);

  // Map current value to the active toggle option
  const groupValue = !value ? "none" : value.startsWith("xB") ? "custom2" : value.startsWith("x") ? value : "custom";

  // FIXED: handle custom2 explicitly before the generic startsWith("x") branch
  const handleGroupChange = (_: unknown, next: string | null) => {
    if (!next) return; // ignore deselect

    if (next === "none") {
      onChange(null);
      return;
    }

    // Lieferwerk -> xB + manual time + dynamic suffix
    if (next === "custom2") {
      const suffix = addition ?? "10+30+0"; // fallback if routeInfo missing
      onChange(`xB${defaultTime}+${suffix}`);
      return;
    }

    // Baustelle -> manual time without prefix
    if (next === "custom") {
      onChange(defaultTime);
      return;
    }

    // Presets like xAM/xPM
    if (next.startsWith("x")) {
      onChange(next);
      return;
    }
  };

  const showTimePicker = !!value && (value.startsWith("xB") || !value.startsWith("x"));

  const timePickerValue = !value ? null : value.startsWith("xB") ? extractManualTimeXB(value) : value;

  const timeWarning = useMemo(() => {
    const earliest = routeInfo?.plantAvailability?.earliestLoadingTime;
    if (!earliest || !timePickerValue || !routeInfo || routeInfo.loadingTime === null || routeInfo.routeTime === null) return null;

    const [hour, minute] = timePickerValue.split(":").map(Number);
    const selectedMinutes = hour * 60 + minute;
    const travelOffset = routeInfo.loadingTime + routeInfo.routeTime;
    const loadingMinutes = value?.startsWith("xB") ? selectedMinutes : selectedMinutes + (routeInfo.inversed ? travelOffset : -travelOffset);
    const [earliestHour, earliestMinute] = earliest.split(":").map(Number);

    if (loadingMinutes >= earliestHour * 60 + earliestMinute) return null;

    const earliestLoadingMinutes = earliestHour * 60 + earliestMinute;
    const earliestSiteMinutes = (earliestLoadingMinutes + (routeInfo.inversed ? -travelOffset : travelOffset) + 24 * 60) % (24 * 60);
    const earliestSiteTime = `${String(Math.floor(earliestSiteMinutes / 60)).padStart(2, "0")}:${String(earliestSiteMinutes % 60).padStart(2, "0")}`;
    const siteHint = value?.startsWith("xB") ? "" : ` Die frühestmögliche Zeit auf der Baustelle ist um ${earliestSiteTime} Uhr.`;

    return `Diese Zeit ist zu früh. Die frühestmögliche Zeit am Lieferwerk ist um ${earliest} Uhr.${siteHint}`;
  }, [routeInfo, timePickerValue, value]);

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "20px" }}>
      <ToggleButtonGroup size="small" orientation="horizontal" exclusive value={groupValue} onChange={handleGroupChange}>
        {imTakt ? (
          <Tooltip title="Im Takt: Keine Änderung">
            <ToggleButton value="none">Im Takt</ToggleButton>
          </Tooltip>
        ) : (
          <Tooltip title="Anschließend: Lieferzeitpunkt wird von der Dispo nach LKW-Verfügbarkeit festgelegt">
            <ToggleButton value="none">anschl.</ToggleButton>
          </Tooltip>
        )}

        <Tooltip title="Vormittags: Lieferzeitpunkt irgendwann am Vormittag">
          <ToggleButton value="xAM">vorm.</ToggleButton>
        </Tooltip>

        <Tooltip title="Nachmittags: Lieferzeitpunkt irgendwann am Nachmittag">
          <ToggleButton value="xPM">nachm.</ToggleButton>
        </Tooltip>

        <Tooltip title="Baustelle: Festgelegter Zeitpunkt an der Baustelle">
          <ToggleButton value="custom">
            <Schedule sx={{ mr: 1 }} />
            Baustelle
          </ToggleButton>
        </Tooltip>

        {(addition !== null || value?.startsWith("xB")) && (
          <Tooltip title="Lieferwerk: Festgelegter Zeitpunkt am Lieferwerk">
            <ToggleButton value="custom2">
              <Schedule sx={{ mr: 1 }} />
              Lieferwerk
            </ToggleButton>
          </Tooltip>
        )}
      </ToggleButtonGroup>

      {showTimePicker ? (
        <div style={{ marginTop: 5 }}>
          <TimePicker
            onChange={(newVal: string | null) => {
              if (!newVal) return;
              if (value?.startsWith("xB")) {
                const [, rest] = splitOnce(value, "+"); // rest may be undefined
                const suffix = addition ?? rest ?? "10+30+0";
                onChange(`xB${newVal}+${suffix}`);
              } else {
                onChange(newVal);
              }
            }}
            className={!value ? "nostart" : ""}
            value={timePickerValue as any}
            disableClock
            clearIcon={null}
          />
        </div>
      ) : null}
      {routeInfo?.plantAvailability?.isClosingDay ? (
        <Alert severity="error" sx={{ width: "100%" }}>
          Das Lieferwerk ist an diesem Tag geschlossen
          {routeInfo.plantAvailability.closingDayComment ? `: ${routeInfo.plantAvailability.closingDayComment}` : "."}
        </Alert>
      ) : timeWarning ? (
        <Alert severity="error" sx={{ width: "100%" }}>
          {timeWarning}
        </Alert>
      ) : null}
    </Box>
  );
}
